<template>
  <div class="expenses-container">
    <!-- Quick Statistics -->
    <el-card class="stats-container">
      <div class="stats-wrapper">
        <div class="stat-item approved-stat" @click="applyStatusFilter(t('expenses.status.approved'))">
          <div class="stat-count">{{ approvedExpenses.length }}</div>
          <div class="stat-info">
            <div class="stat-title">{{ t('expenses.stats.approved') }}</div>
            <div class="stat-amount">{{ formatCurrency(approvedExpensesTotal) }}</div>
          </div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item pending-stat" @click="applyStatusFilter(t('expenses.status.pending'))">
          <div class="stat-count">{{ pendingExpenses.length }}</div>
          <div class="stat-info">
            <div class="stat-title">{{ t('expenses.stats.pending') }}</div>
            <div class="stat-amount">{{ formatCurrency(pendingExpensesTotal) }}</div>
          </div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item paid-stat" @click="applyStatusFilter(t('expenses.status.paid'))">
          <div class="stat-count">{{ paidExpenses.length }}</div>
          <div class="stat-info">
            <div class="stat-title">{{ t('expenses.stats.paid') }}</div>
            <div class="stat-amount">{{ formatCurrency(paidExpensesTotal) }}</div>
          </div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item rejected-stat" @click="applyStatusFilter(t('expenses.status.rejected'))">
          <div class="stat-count">{{ rejectedExpenses.length }}</div>
          <div class="stat-info">
            <div class="stat-title">{{ t('expenses.stats.rejected') }}</div>
            <div class="stat-amount">{{ formatCurrency(rejectedExpensesTotal) }}</div>
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- Filter Container -->
    <div class="filter-wrapper">
      <el-card class="filter-container" :body-style="{ padding: '0px' }">
        <div class="filter-primary-row">
          <!-- Left section: Add and Import buttons -->
          <div class="filter-section left-section">
            <el-button type="primary" class="add-expense-btn" @click="showAddModal = true">
              <el-icon><Plus /></el-icon>
              {{ t('expenses.actions.add') }}
            </el-button>
            <el-dropdown trigger="click">
              <el-button class="import-btn" type="success">
                <el-icon><Upload /></el-icon>
                {{ t('expenses.actions.import') }}
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item @click="importFromExcel">
                    <el-icon><Document /></el-icon>
                    Excel
                  </el-dropdown-item>
                  <el-dropdown-item @click="downloadExcelTemplate" divided>
                    <el-icon><Download /></el-icon>
                    {{ t('expenses.import.downloadTemplate') }}
                  </el-dropdown-item>
                  <el-dropdown-item @click="showTemplateInfo">
                    <el-icon><InfoFilled /></el-icon>
                    {{ t('expenses.import.templateInfoTitle') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <input
              ref="excelUploadInput"
              type="file"
              accept=".xlsx, .xls"
              style="display: none"
              @change="handleExcelUpload"
            />
          </div>
          
          <!-- Center section: Quick filters and action buttons -->
          <div class="filter-section search-section">
            <div class="search-container">
              <div class="filter-item date-filter">
                <el-date-picker
                  v-model="dateRange"
                  type="daterange"
                  range-separator=" → "
                  :start-placeholder="t('expenses.filters.startDate')"
                  :end-placeholder="t('expenses.filters.endDate')"
                  format="DD/MM/YYYY"
                  value-format="YYYY-MM-DD"
                  @change="handleDateRangeChange"
                  class="date-range-picker"
                />
              </div>
              
              <div class="filter-item">
                <el-select 
                  v-model="filterForm.expenseType" 
                  :placeholder="t('expenses.filters.expenseType')" 
                  clearable 
                  @change="applyFilters"
                  class="compact-select"
                >
                  <el-option
                    v-for="type in expenseTypes"
                    :key="type"
                    :label="type"
                    :value="type"
                  />
                </el-select>
              </div>
              
              <div class="filter-item">
                <el-select 
                  v-model="filterForm.category" 
                  :placeholder="t('expenses.filters.category')" 
                  clearable 
                  @change="applyFilters"
                  class="compact-select"
                >
                  <el-option
                    v-for="category in categories"
                    :key="category.id"
                    :label="category.name"
                    :value="category.name"
                  />
                </el-select>
              </div>
              
              <div class="filter-item">
                <el-select
                  v-model="filterForm.status"
                  :placeholder="t('expenses.filters.status')"
                  clearable
                  @change="applyFilters"
                  class="compact-select"
                >
                  <el-option :label="t('expenses.status.pending')" :value="t('expenses.status.pending')" />
                  <el-option :label="t('expenses.status.approved')" :value="t('expenses.status.approved')" />
                  <el-option :label="t('expenses.status.paid')" :value="t('expenses.status.paid')" />
                  <el-option :label="t('expenses.status.rejected')" :value="t('expenses.status.rejected')" />
                </el-select>
              </div>
              
              <div class="action-buttons">
                <el-tooltip :content="t('common.filters.search')" placement="top" :show-after="500">
                  <el-button type="primary" @click="applyFilters" class="action-button">
                    <el-icon><Search /></el-icon>
                  </el-button>
                </el-tooltip>
                <el-tooltip :content="t('common.filters.reset')" placement="top" :show-after="500">
                  <el-button @click="resetFilters" class="action-button">
                    <el-icon><RefreshRight /></el-icon>
                  </el-button>
                </el-tooltip>
                <el-tooltip :content="t('common.filters.advanced')" placement="top" :show-after="500">
                  <el-button 
                    type="info" 
                    @click="toggleFilterForm" 
                    class="action-button"
                    :class="{ 'is-active': filterFormVisible }"
                  >
                    <el-icon><Filter /></el-icon>
                  </el-button>
                </el-tooltip>
                <el-tooltip :content="t('common.filters.columnDisplay')" placement="top" :show-after="500">
                  <el-dropdown trigger="click" @command="handleColumnToggle" class="column-selector">
                    <el-button class="action-button">
                      <el-icon><Grid /></el-icon>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item 
                          v-for="col in availableColumns" 
                          :key="col.value"
                          :command="col.value"
                          :class="{ 'is-checked': visibleColumns.includes(col.value) }"
                        >
                          <el-checkbox 
                            v-model="visibleColumns" 
                            :label="col.value"
                            @change="handleColumnVisibilityChange"
                          >
                            {{ col.label }}
                          </el-checkbox>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>

        <!-- Advanced filters -->
        <el-collapse-transition>
          <div v-if="filterFormVisible" class="filter-advanced">
            <el-form :model="filterForm" class="advanced-form">
              <el-row :gutter="16">
                <el-col :span="8">
                  <el-form-item :label="t('expenses.filters.accountHolder')">
                    <el-input 
                      v-model="filterForm.accountHolder" 
                      :placeholder="t('expenses.filters.accountHolder')" 
                      clearable 
                      @input="applyFilters"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item :label="t('expenses.filters.accountNumber')">
                    <el-input 
                      v-model="filterForm.accountNumber" 
                      :placeholder="t('expenses.filters.accountNumber')" 
                      clearable 
                      @input="applyFilters"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item :label="t('expenses.filters.bankName')">
                    <el-input 
                      v-model="filterForm.bankName" 
                      :placeholder="t('expenses.filters.bankName')" 
                      clearable 
                      @input="applyFilters"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="16">
                <el-col :span="8">
                  <el-form-item :label="t('expenses.filters.minAmount')">
                    <el-input-number 
                      v-model="filterForm.minAmount" 
                      :min="0" 
                      :placeholder="t('expenses.filters.minAmount')"
                      @change="applyFilters"
                      class="full-width"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item :label="t('expenses.filters.maxAmount')">
                    <el-input-number 
                      v-model="filterForm.maxAmount" 
                      :min="0" 
                      :placeholder="t('expenses.filters.maxAmount')"
                      @change="applyFilters"
                      class="full-width"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item :label="t('expenses.filters.note')">
                    <el-input 
                      v-model="filterForm.note" 
                      :placeholder="t('expenses.filters.note')" 
                      clearable 
                      @input="applyFilters"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </div>
        </el-collapse-transition>

        <!-- Active filters -->
        <div class="active-filters" v-if="hasActiveFilters">
          <div class="filter-label">{{ t('common.filters.active') }}</div>
          <el-tag 
            v-if="filterForm.startDate" 
            closable 
            @close="clearStartDate" 
            size="small"
          >
            {{ t('expenses.filters.startDate') }}: {{ formatDate(filterForm.startDate) }}
          </el-tag>
          <el-tag 
            v-if="filterForm.endDate" 
            closable 
            @close="clearEndDate" 
            size="small"
          >
            {{ t('expenses.filters.endDate') }}: {{ formatDate(filterForm.endDate) }}
          </el-tag>
          <el-tag 
            v-if="filterForm.expenseType" 
            closable 
            @close="clearExpenseType" 
            size="small"
          >
            {{ t('expenses.filters.expenseType') }}: {{ filterForm.expenseType }}
          </el-tag>
          <el-tag 
            v-if="filterForm.category" 
            closable 
            @close="clearCategory" 
            size="small"
          >
            {{ t('expenses.filters.category') }}: {{ filterForm.category }}
          </el-tag>
          <el-tag 
            v-if="filterForm.status" 
            closable 
            @close="clearStatus" 
            size="small"
            :type="getStatusType(filterForm.status)"
          >
            {{ t('expenses.filters.status') }}: {{ filterForm.status }}
          </el-tag>
          <el-tag 
            v-if="filterForm.accountHolder" 
            closable 
            @close="clearAccountHolder" 
            size="small"
          >
            {{ t('expenses.filters.accountHolder') }}: {{ filterForm.accountHolder }}
          </el-tag>
          <el-tag 
            v-if="filterForm.accountNumber" 
            closable 
            @close="clearAccountNumber" 
            size="small"
          >
            {{ t('expenses.filters.accountNumber') }}: {{ filterForm.accountNumber }}
          </el-tag>
          <el-tag 
            v-if="filterForm.bankName" 
            closable 
            @close="clearBankName" 
            size="small"
          >
            {{ t('expenses.filters.bankName') }}: {{ filterForm.bankName }}
          </el-tag>
          <el-tag 
            v-if="filterForm.minAmount" 
            closable 
            @close="clearMinAmount" 
            size="small"
          >
            {{ t('expenses.filters.minAmount') }}: {{ formatCurrency(filterForm.minAmount) }}
          </el-tag>
          <el-tag 
            v-if="filterForm.maxAmount" 
            closable 
            @close="clearMaxAmount" 
            size="small"
          >
            {{ t('expenses.filters.maxAmount') }}: {{ formatCurrency(filterForm.maxAmount) }}
          </el-tag>
          <el-tag 
            v-if="filterForm.note" 
            closable 
            @close="clearNote" 
            size="small"
          >
            {{ t('expenses.filters.note') }}: {{ filterForm.note }}
          </el-tag>
        </div>
      </el-card>
    </div>

    <!-- Expenses Table -->
    <el-card class="table-container">
      <div class="table-header">
        <div class="table-left-actions">
          <div class="total-section">
            <span>{{ t('expenses.stats.total') }}: <strong>{{ formatCurrency(totalAmount) }}</strong></span>
          </div>
        </div>
        <div class="filter-tools">
          <el-button 
            v-if="selectedRows.length > 0" 
            type="danger" 
            class="icon-button"
            @click="confirmDeleteSelected"
          >
            <el-icon><Delete /></el-icon>
            <span class="button-badge">{{ selectedRows.length }}</span>
          </el-button>
          
          <el-popover
            placement="bottom-end"
            :width="300"
            trigger="click"
            v-model:visible="columnSelectorVisible"
          >
            <template #reference>
              <el-button type="primary" class="icon-button">
                <el-icon><Setting /></el-icon>
              </el-button>
            </template>
            
            <template #default>
              <div class="column-selector-content">
                <div class="column-selector-header">
                  <span class="column-selector-title">{{ t('common.filters.columnDisplay') }}</span>
                  <div class="column-selector-actions">
                    <el-button 
                      type="primary" 
                      link 
                      size="small" 
                      @click="selectAllColumns"
                    >
                      {{ t('common.actions.selectAll') }}
                    </el-button>
                    <el-button 
                      type="danger" 
                      link 
                      size="small" 
                      @click="deselectAllColumns"
                    >
                      {{ t('common.actions.deselectAll') }}
                    </el-button>
                  </div>
                </div>
                <el-divider style="margin: 8px 0" />
                <el-checkbox-group v-model="visibleColumns" class="column-selector-list">
                  <div class="column-selector-item" v-for="column in availableColumns" :key="column.value">
                    <el-checkbox :label="column.value">{{ column.label }}</el-checkbox>
                  </div>
                </el-checkbox-group>
                <el-divider style="margin: 8px 0" />
                <div class="column-selector-footer">
                  <el-button size="small" @click="columnSelectorVisible = false">Đóng</el-button>
                  <el-button size="small" type="primary" @click="saveColumnPreferences">Lưu</el-button>
                </div>
              </div>
            </template>
          </el-popover>
        </div>
      </div>
      
      <el-table 
        ref="expensesTable"
        :data="serverPagination ? paginatedExpenses : displayExpenses" 
        stripe 
        style="width: 100%"
        @selection-change="handleSelectionChange"
        v-loading="loading"
        :highlight-current-row="true"
        height="calc(100vh - 450px)"
        :default-sort="{ prop: 'payment_date', order: 'descending' }"
        @sort-change="handleSortChange"
        border
        class="expense-table"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column
          v-if="visibleColumns.includes('payment_date')"
          prop="payment_date"
          label="Ngày thanh toán"
          sortable
          width="150"
        >
          <template #default="{ row }">
            {{ formatDate(row.payment_date) }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="visibleColumns.includes('expense_type')"
          prop="expense_type"
          label="Loại chi phí"
          sortable
          min-width="120"
        />

        <el-table-column
          v-if="visibleColumns.includes('category')"
          prop="category"
          label="Danh mục"
          sortable
          min-width="120"
        />

        <el-table-column
          v-if="visibleColumns.includes('description')"
          prop="description"
          label="Mô tả"
          min-width="200"
          show-overflow-tooltip
        />

        <el-table-column
          v-if="visibleColumns.includes('amount')"
          prop="amount"
          label="Số tiền"
          sortable
          width="150"
          align="right"
        >
          <template #default="{ row }">
            {{ formatCurrency(row.amount) }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="visibleColumns.includes('account_holder')"
          prop="account_holder"
          label="Chủ tài khoản"
          min-width="150"
          show-overflow-tooltip
        />

        <el-table-column
          v-if="visibleColumns.includes('account_number')"
          prop="account_number"
          label="Số tài khoản"
          min-width="150"
          show-overflow-tooltip
        />

        <el-table-column
          v-if="visibleColumns.includes('bank_name')"
          prop="bank_name"
          label="Ngân hàng"
          min-width="120"
          show-overflow-tooltip
        />

        <el-table-column
          v-if="visibleColumns.includes('status')"
          prop="status"
          label="Trạng thái"
          width="130"
        >
          <template #default="{ row }">
            <el-tag
              :type="getStatusType(row.status)"
              effect="light"
              class="status-tag"
            >
              {{ row.status }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          v-if="visibleColumns.includes('document')"
          prop="document"
          label="Tài liệu"
          width="100"
        >
          <template #default="{ row }">
            <el-button
              v-if="row.document"
              type="primary"
              link
              @click="downloadDocument(row.document)"
            >
              <el-icon><Document /></el-icon>
              Tải về
            </el-button>
          </template>
        </el-table-column>

        <el-table-column
          v-if="visibleColumns.includes('invoice_pdf')"
          prop="invoice_pdf"
          label="Hóa đơn PDF"
          width="120"
        >
          <template #default="{ row }">
            <el-button
              v-if="row.invoice_pdf"
              type="primary"
              link
              @click="downloadDocument(row.invoice_pdf)"
            >
              <el-icon><Document /></el-icon>
              PDF
            </el-button>
          </template>
        </el-table-column>

        <el-table-column
          v-if="visibleColumns.includes('invoice_xml')"
          prop="invoice_xml"
          label="Hóa đơn XML"
          width="120"
        >
          <template #default="{ row }">
            <el-button
              v-if="row.invoice_xml"
              type="primary"
              link
              @click="downloadDocument(row.invoice_xml)"
            >
              <el-icon><Document /></el-icon>
              XML
            </el-button>
          </template>
        </el-table-column>

        <el-table-column
          v-if="visibleColumns.includes('note')"
          prop="note"
          label="Ghi chú"
          min-width="150"
          show-overflow-tooltip
        />

        <el-table-column
          fixed="right"
          label="Thao tác"
          width="150"
          align="center"
        >
          <template #default="{ row }">
            <el-button
              type="primary"
              link
              @click="handleEdit(row)"
            >
              <el-icon><Edit /></el-icon>
              Sửa
            </el-button>
            <el-button
              type="danger"
              link
              @click="handleDelete(row)"
            >
              <el-icon><Delete /></el-icon>
              Xóa
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- Pagination -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="serverPagination ? total : filteredExpenses.length"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          background
          class="expense-pagination"
        />
      </div>
    </el-card>

    <!-- Add/Edit Expense Modal -->
    <el-dialog
      v-model="showAddModal"
      :title="editingExpense ? t('expenses.actions.edit') : t('expenses.actions.add')"
      width="40%"
    >
      <el-form 
        :model="expenseForm" 
        label-width="140px"
        label-position="top"
      >
        <el-form-item :label="t('expenses.form.paymentDate')" required>
          <el-date-picker
            v-model="expenseForm.payment_date"
            type="date"
            :placeholder="t('expenses.form.paymentDate')"
            style="width: 100%"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item :label="t('expenses.form.expenseType')" required>
          <el-select 
            v-model="expenseForm.expense_type" 
            :placeholder="t('expenses.form.expenseType')" 
            filterable 
            allow-create 
            default-first-option
            style="width: 100%"
          >
            <el-option
              v-for="type in expenseTypes"
              :key="type"
              :label="type"
              :value="type"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="t('expenses.form.category')" required>
          <el-select v-model="expenseForm.category" style="width: 100%">
            <el-option
              v-for="category in categories"
              :key="category.id"
              :label="category.name"
              :value="category.name"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="t('expenses.form.description')">
          <el-input v-model="expenseForm.description" type="textarea" rows="3" />
        </el-form-item>
        <el-form-item :label="t('expenses.form.amount')" required>
          <el-input-number v-model="expenseForm.amount" :min="0" :precision="0" style="width: 100%" />
        </el-form-item>
        <el-form-item :label="t('expenses.form.accountHolder')" required>
          <el-input v-model="expenseForm.account_holder" />
        </el-form-item>
        <el-form-item :label="t('expenses.form.status')">
          <el-select v-model="expenseForm.status" style="width: 100%">
            <el-option :label="t('expenses.status.pending')" :value="t('expenses.status.pending')" />
            <el-option :label="t('expenses.status.approved')" :value="t('expenses.status.approved')" />
            <el-option :label="t('expenses.status.paid')" :value="t('expenses.status.paid')" />
            <el-option :label="t('expenses.status.rejected')" :value="t('expenses.status.rejected')" />
          </el-select>
        </el-form-item>
        <el-form-item :label="t('expenses.form.accountNumber')">
          <el-input v-model="expenseForm.account_number" :placeholder="t('expenses.form.accountNumber')" />
        </el-form-item>
        <el-form-item :label="t('expenses.form.bankName')">
          <el-input v-model="expenseForm.bank_name" :placeholder="t('expenses.form.bankName')" />
        </el-form-item>
        <el-form-item :label="t('expenses.form.note')">
          <el-input v-model="expenseForm.note" type="textarea" rows="2" :placeholder="t('expenses.form.note')" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddModal = false">{{ t('common.cancel') }}</el-button>
          <el-button type="primary" @click="saveExpense">
            {{ editingExpense ? t('common.actions.update') : t('common.actions.add') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, reactive, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import { 
  Plus, Edit, Delete, Search, RefreshRight, Setting, 
  ArrowUp, ArrowDown, Filter, Document, ArrowRight, Grid, Upload, InfoFilled, Download
} from '@element-plus/icons-vue'
import axios from 'axios'
import { format } from 'date-fns'
import { API_URLS, API_VERSION } from '@/config'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { useI18n } from 'vue-i18n'

// Initialize i18n with composition API
const { t } = useI18n()

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface Expense {
  id: string;
  payment_date: string;
  expense_type?: string;
  category: string;
  description?: string;
  amount: number;
  account_holder: string;
  status?: string;
  document?: string;
  invoice_pdf?: string;
  invoice_xml?: string;
  note?: string;
  account_number?: string;
  bank_name?: string;
}

interface ColumnConfig {
  label: string;
  value: string;
  defaultVisible?: boolean;
}

interface SortParams {
  prop: string;
  order: 'ascending' | 'descending' | null;
}

interface FilterForm {
  startDate: string;
  endDate: string;
  expenseType: string;
  category: string;
  status: string;
  accountHolder: string;
  accountNumber: string;
  bankName: string;
  note: string;
  minAmount: number | undefined;
  maxAmount: number | undefined;
}

const expenses = ref<Expense[]>([])
const categories = ref<Category[]>([])
const expenseTypes = ref<string[]>([])
const showAddModal = ref(false)
const showColumnSelector = ref(false)
const editingExpense = ref<Expense | null>(null)
const loading = ref(false)
const expensesTable = ref()

const expenseForm = ref({
  payment_date: '',
  expense_type: '',
  category: '',
  description: '',
  amount: 0,
  account_holder: '',
  status: 'Chờ duyệt',
  note: '',
  account_number: '',
  bank_name: ''
})

// Pagination
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const paginatedExpenses = ref<Expense[]>([])
const serverPagination = ref(true)

const handleSizeChange = (val: number) => {
  pageSize.value = val
  if (serverPagination.value) {
    fetchExpensesWithFilters()
  }
  // Store user preference
  localStorage.setItem('expensePageSize', val.toString())
}

const handleCurrentChange = (val: number) => {
  currentPage.value = val
  if (serverPagination.value) {
    fetchExpensesWithFilters()
  }
  
  // Scroll to top of table when page changes
  nextTick(() => {
    const tableEl = document.querySelector('.expense-table .el-table__body-wrapper')
    if (tableEl) {
      tableEl.scrollTop = 0
    }
  })
}

// Column configuration
const availableColumns = [
  { label: 'Ngày thanh toán', value: 'payment_date', defaultVisible: true },
  { label: 'Loại chi phí', value: 'expense_type', defaultVisible: true },
  { label: 'Danh mục', value: 'category', defaultVisible: true },
  { label: 'Mô tả', value: 'description', defaultVisible: true },
  { label: 'Số tiền', value: 'amount', defaultVisible: true },
  { label: 'Chủ tài khoản', value: 'account_holder', defaultVisible: true },
  { label: 'Số tài khoản', value: 'account_number', defaultVisible: false },
  { label: 'Ngân hàng', value: 'bank_name', defaultVisible: false },
  { label: 'Trạng thái', value: 'status', defaultVisible: true },
  { label: 'Tài liệu', value: 'document', defaultVisible: false },
  { label: 'Hóa đơn PDF', value: 'invoice_pdf', defaultVisible: false },
  { label: 'Hóa đơn XML', value: 'invoice_xml', defaultVisible: false },
  { label: 'Ghi chú', value: 'note', defaultVisible: false }
] as ColumnConfig[];

const visibleColumns = ref<string[]>([])

// Filtering
const filterForm = reactive<FilterForm>({
  startDate: '',
  endDate: '',
  expenseType: '',
  category: '',
  status: '',
  accountHolder: '',
  accountNumber: '',
  bankName: '',
  note: '',
  minAmount: undefined,
  maxAmount: undefined
})

const filteredExpenses = computed(() => {
  let result = [...expenses.value]
  
  if (filterForm.startDate) {
    result = result.filter(expense => expense.payment_date >= filterForm.startDate)
  }
  
  if (filterForm.endDate) {
    result = result.filter(expense => expense.payment_date <= filterForm.endDate)
  }
  
  if (filterForm.expenseType) {
    result = result.filter(expense => expense.expense_type === filterForm.expenseType)
  }
  
  if (filterForm.category) {
    result = result.filter(expense => expense.category === filterForm.category)
  }
  
  if (filterForm.status) {
    result = result.filter(expense => expense.status === filterForm.status)
  }
  
  if (filterForm.accountHolder) {
    result = result.filter(expense => 
      expense.account_holder && 
      expense.account_holder.toLowerCase().includes(filterForm.accountHolder.toLowerCase())
    )
  }
  
  if (filterForm.accountNumber) {
    result = result.filter(expense => 
      expense.account_number && 
      expense.account_number.toLowerCase().includes(filterForm.accountNumber.toLowerCase())
    )
  }
  
  if (filterForm.bankName) {
    result = result.filter(expense => 
      expense.bank_name && 
      expense.bank_name.toLowerCase().includes(filterForm.bankName.toLowerCase())
    )
  }
  
  if (filterForm.note) {
    result = result.filter(expense => 
      expense.note && 
      expense.note.toLowerCase().includes(filterForm.note.toLowerCase())
    )
  }
  
  if (filterForm.minAmount !== undefined && filterForm.minAmount !== null) {
    result = result.filter(expense => expense.amount >= filterForm.minAmount!)
  }
  
  if (filterForm.maxAmount !== undefined && filterForm.maxAmount !== null) {
    result = result.filter(expense => expense.amount <= filterForm.maxAmount!)
  }
  
  return result
})

// Calculate total amount - update to work with both server and client pagination
const totalAmount = computed(() => {
  if (serverPagination.value) {
    // If we're using server pagination but want to show total of all expenses
    return expenses.value.reduce((sum, expense) => sum + expense.amount, 0)
  } else {
    // If we're filtering client-side
    return filteredExpenses.value.reduce((sum, expense) => sum + expense.amount, 0)
  }
})

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount)
}

// Computed property để hiển thị dữ liệu phân trang
const displayExpenses = computed(() => {
  if (!filteredExpenses.value || filteredExpenses.value.length === 0) {
    return []
  }
  
  const startIndex = (currentPage.value - 1) * pageSize.value
  return filteredExpenses.value.slice(startIndex, startIndex + pageSize.value)
})

// Hàm để lấy dữ liệu phân trang từ server
const fetchExpensesWithFilters = async () => {
  loading.value = true
  try {
    // Xây dựng query params từ các bộ lọc
    const params = new URLSearchParams()
    
    // Thêm các tham số phân trang
    params.append('page', currentPage.value.toString())
    params.append('page_size', pageSize.value.toString())
    
    // Thêm tham số sắp xếp nếu có
    if (currentSort.value.prop && currentSort.value.order) {
      params.append('sort_by', currentSort.value.prop)
      params.append('sort_order', currentSort.value.order)
    }
    
    // Date range
    if (filterForm.startDate) {
      params.append('start_date', filterForm.startDate)
    }
    
    if (filterForm.endDate) {
      params.append('end_date', filterForm.endDate)
    }
    
    // Thêm các tham số lọc khác nếu có giá trị
    if (filterForm.expenseType) {
      params.append('expense_type', filterForm.expenseType)
    }
    
    if (filterForm.category) {
      params.append('category', filterForm.category)
    }
    
    if (filterForm.status) {
      params.append('status', filterForm.status)
    }
    
    if (filterForm.accountHolder) {
      params.append('account_holder', filterForm.accountHolder)
    }
    
    if (filterForm.accountNumber) {
      params.append('account_number', filterForm.accountNumber)
    }
    
    if (filterForm.bankName) {
      params.append('bank_name', filterForm.bankName)
    }
    
    if (filterForm.note) {
      params.append('note', filterForm.note)
    }
    
    if (filterForm.minAmount !== undefined && filterForm.minAmount !== null) {
      params.append('min_amount', String(filterForm.minAmount))
    }
    
    if (filterForm.maxAmount !== undefined && filterForm.maxAmount !== null) {
      params.append('max_amount', String(filterForm.maxAmount))
    }
    
    // Log the API call with params (helpful for debugging)
    console.log(`Fetching expenses with: ${params.toString()}`)
    
    const response = await axios.get(`${API_URLS.expenses}/${API_VERSION}/expenses`, { params })
    
    if (response.data && response.data.items && Array.isArray(response.data.items)) {
      // Format phản hồi dạng phân trang từ server
      paginatedExpenses.value = response.data.items
      total.value = response.data.total || response.data.items.length
      serverPagination.value = true
    } else if (Array.isArray(response.data)) {
      // API trả về mảng trực tiếp (không có phân trang)
      paginatedExpenses.value = response.data
      expenses.value = response.data
      total.value = response.data.length
      serverPagination.value = false
      
      // Extract unique expense types for filter dropdown
      updateExpenseTypes(response.data)
    } else {
      console.error('Unexpected response format:', response.data)
      ElMessage.error('Định dạng dữ liệu không đúng')
      paginatedExpenses.value = []
      total.value = 0
      serverPagination.value = false
    }
  } catch (error) {
    console.error('Error fetching expenses with filters:', error)
    ElMessage.error('Không thể tải danh sách chi phí với bộ lọc')
    serverPagination.value = false
    paginatedExpenses.value = []
  } finally {
    loading.value = false
  }
}

// Cập nhật fetchExpenses để sử dụng fetchExpensesWithFilters
const fetchExpenses = async () => {
  try {
    // Tải tất cả chi phí cho dashboard
    try {
      const fullResponse = await axios.get(`${API_URLS.expenses}/${API_VERSION}/expenses/all`);
      expenses.value = fullResponse.data;
    } catch (error) {
      console.warn('Error fetching from /expenses/all, falling back to main endpoint:', error);
      // Fallback: nếu endpoint /expenses/all không hoạt động, sử dụng endpoint chính
      const fallbackResponse = await axios.get(`${API_URLS.expenses}/${API_VERSION}/expenses`);
      expenses.value = fallbackResponse.data.items || fallbackResponse.data;
    }
    
    // Extract unique expense types for filter dropdown
    const uniqueTypes = new Set<string>()
    expenses.value.forEach(expense => {
      if (expense.expense_type) {
        uniqueTypes.add(expense.expense_type)
      }
    })
    expenseTypes.value = Array.from(uniqueTypes)
    
    // Tải dữ liệu phân trang
    await fetchExpensesWithFilters()
    
  } catch (error) {
    console.error('Error fetching expenses:', error)
    ElMessage.error('Không thể tải danh sách chi phí')
    serverPagination.value = false
  }
}

// Filter functions
const applyFilters = async () => {
  loading.value = true;
  try {
    currentPage.value = 1; // Reset to first page when applying filters
    
    // Build query parameters
    const params = new URLSearchParams();
    
    // Date range
    if (filterForm.startDate) {
      params.append('start_date', filterForm.startDate);
    }
    if (filterForm.endDate) {
      params.append('end_date', filterForm.endDate);
    }
    
    // Basic filters
    if (filterForm.expenseType) {
      params.append('expense_type', filterForm.expenseType.trim());
    }
    if (filterForm.category) {
      params.append('category', filterForm.category.trim());
    }
    if (filterForm.status) {
      params.append('status', filterForm.status.trim());
    }
    
    // Text search filters
    if (filterForm.accountHolder) {
      params.append('account_holder', filterForm.accountHolder.trim());
    }
    if (filterForm.accountNumber) {
      params.append('account_number', filterForm.accountNumber.trim());
    }
    if (filterForm.bankName) {
      params.append('bank_name', filterForm.bankName.trim());
    }
    if (filterForm.note) {
      params.append('note', filterForm.note.trim());
    }
    
    // Amount range
    if (filterForm.minAmount !== undefined && filterForm.minAmount !== null) {
      params.append('min_amount', filterForm.minAmount.toString());
    }
    if (filterForm.maxAmount !== undefined && filterForm.maxAmount !== null) {
      params.append('max_amount', filterForm.maxAmount.toString());
    }
    
    // Pagination
    params.append('page', currentPage.value.toString());
    params.append('page_size', pageSize.value.toString());
    
    // Call API with filters
    const response = await axios.get(`${API_URLS.expenses}/${API_VERSION}/expenses`, { params });
    
    if (response.data.items && Array.isArray(response.data.items)) {
      paginatedExpenses.value = response.data.items;
      total.value = response.data.total || response.data.items.length;
      
      // Update the full expenses list for statistics if needed
      if (!serverPagination.value) {
        expenses.value = response.data.items;
      }
    } else {
      paginatedExpenses.value = response.data;
      total.value = response.data.length;
      expenses.value = response.data;
      serverPagination.value = false;
    }
    
  } catch (error) {
    console.error('Error applying filters:', error);
    ElMessage.error('Có lỗi xảy ra khi lọc dữ liệu');
  } finally {
    loading.value = false;
  }
};

const resetFilters = () => {
  // Type-safe way to reset filter form fields
  filterForm.startDate = '';
  filterForm.endDate = '';
  filterForm.expenseType = '';
  filterForm.category = '';
  filterForm.status = '';
  filterForm.accountHolder = '';
  filterForm.accountNumber = '';
  filterForm.bankName = '';
  filterForm.note = '';
  filterForm.minAmount = undefined;
  filterForm.maxAmount = undefined;
  
  // Reset date range picker
  dateRange.value = null;
  
  // Reset pagination
  currentPage.value = 1;
  
  // Apply the reset filters
  applyFilters();
  
  ElMessage.success('Đã làm mới bộ lọc');
};

// Update the debounce function with proper typing
function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  // Use arrow function to avoid 'this' context issues
  return (...args: Parameters<T>): void => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

// Add watch for filter changes
watch([
  () => filterForm.accountHolder,
  () => filterForm.accountNumber,
  () => filterForm.bankName,
  () => filterForm.note
], () => {
  debouncedApplyFilters();
});

// Add debounced filter function for text inputs
const debouncedApplyFilters = debounce(() => {
  applyFilters();
}, 300);

// Update the status filter function
const applyStatusFilter = (status: string) => {
  resetFilters(); // Reset all filters first
  filterForm.status = status;
  applyFilters();
  
  // Scroll to table
  nextTick(() => {
    const tableEl = document.querySelector('.table-container');
    if (tableEl) {
      tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
};

// Column customization
const saveColumnPreferences = () => {
  localStorage.setItem('expenseVisibleColumns', JSON.stringify(visibleColumns.value))
  columnSelectorVisible.value = false
  ElMessage.success('Đã lưu tùy chỉnh cột')
}

// Load user preferences if available
const savedColumns = localStorage.getItem('expenseVisibleColumns');

// Thêm biến để kiểm soát hiển thị form filter
const filterFormVisible = ref(false)
const dateRange = ref<[string, string] | null>(null)

// Thêm các functions xử lý filter
const clearStartDate = () => {
  filterForm.startDate = ''
  updateDateRange()
  applyFilters()
}

const clearEndDate = () => {
  filterForm.endDate = ''
  updateDateRange()
  applyFilters()
}

// Cập nhật dateRange dựa trên filterForm
const updateDateRange = () => {
  if (filterForm.startDate && filterForm.endDate) {
    dateRange.value = [filterForm.startDate, filterForm.endDate]
  } else {
    dateRange.value = null
  }
}

// Helper function để toggle form filter
const toggleFilterForm = () => {
  filterFormVisible.value = !filterFormVisible.value
}

// Xử lý date range picker
const handleDateRangeChange = (val: [string, string] | null) => {
  if (val) {
    filterForm.startDate = val[0]
    filterForm.endDate = val[1]
  } else {
    filterForm.startDate = ''
    filterForm.endDate = ''
  }
  applyFilters()
}

// Format date hiển thị đơn giản
const formatSimpleDate = (dateString: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('vi-VN')
}

// Helper functions để clear từng bộ lọc riêng
const clearExpenseType = () => {
  filterForm.expenseType = ''
  applyFilters()
}

const clearCategory = () => {
  filterForm.category = ''
  applyFilters()
}

const clearStatus = () => {
  filterForm.status = ''
  applyFilters()
}

const clearAccountHolder = () => {
  filterForm.accountHolder = ''
  applyFilters()
}

const clearAccountNumber = () => {
  filterForm.accountNumber = ''
  applyFilters()
}

const clearBankName = () => {
  filterForm.bankName = ''
  applyFilters()
}

const clearNote = () => {
  filterForm.note = ''
  applyFilters()
}

const clearMinAmount = () => {
  filterForm.minAmount = undefined
  applyFilters()
}

const clearMaxAmount = () => {
  filterForm.maxAmount = undefined
  applyFilters()
}

// Kiểm tra xem có filter nào đang được áp dụng không
const hasActiveFilters = computed(() => {
  return filterForm.startDate || 
         filterForm.endDate || 
         filterForm.expenseType || 
         filterForm.category ||
         filterForm.status ||
         filterForm.accountHolder ||
         filterForm.accountNumber ||
         filterForm.bankName ||
         filterForm.note ||
         filterForm.minAmount !== undefined ||
         filterForm.maxAmount !== undefined
})

// Load date range khi có filter
watch([() => filterForm.startDate, () => filterForm.endDate], ([newStart, newEnd]) => {
  dateRange.value = [newStart, newEnd]
})

// Thêm biến để theo dõi các hàng được chọn
const selectedRows = ref<Expense[]>([])

// Xử lý khi người dùng thay đổi lựa chọn
const handleSelectionChange = (selection: Expense[]) => {
  selectedRows.value = selection
}

// Xóa các dòng được chọn
const deleteSelectedRows = async () => {
  try {
    // Tạo mảng các promises để xóa từng hàng
    const deletePromises = selectedRows.value.map(row => 
      axios.delete(`${API_URLS.expenses}/${API_VERSION}/expenses/${row.id}`)
    )
    
    // Đợi tất cả đều hoàn thành
    await Promise.all(deletePromises)
    
    // Hiển thị thông báo thành công
    ElMessage.success(`Đã xóa ${selectedRows.value.length} chi phí`)
    
    // Cập nhật lại danh sách
    await fetchExpenses()
    
    // Reset lựa chọn
    selectedRows.value = []
  } catch (error) {
    console.error('Error deleting expenses:', error)
    ElMessage.error('Có lỗi xảy ra khi xóa các chi phí')
  }
}

// Thêm biến để quản lý popover chọn cột
const columnSelectorVisible = ref(false)

// Hàm xác nhận xóa nhiều dòng
const confirmDeleteSelected = () => {
  ElMessageBox.confirm(
    `Bạn có chắc chắn muốn xóa ${selectedRows.value.length} chi phí đã chọn?`,
    'Xác nhận xóa',
    {
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy',
      type: 'warning',
    }
  )
  .then(() => {
    deleteSelectedRows()
  })
  .catch(() => {
    // Người dùng đã hủy thao tác
  })
}

// Add new functions for select/deselect all
const selectAllColumns = () => {
  visibleColumns.value = availableColumns.map(column => column.value)
}

const deselectAllColumns = () => {
  visibleColumns.value = []
}

// Add function to get status type for coloring tags
const getStatusType = (status: string): string => {
  const statusMap: Record<string, string> = {
    'Chờ duyệt': 'warning',
    'Đã duyệt': 'success',
    'Đã thanh toán': 'primary',
    'Từ chối': 'danger'
  }
  return statusMap[status] || 'info'
}

// Add the computed properties for statistics
const approvedExpenses = computed(() => {
  return expenses.value.filter(expense => expense.status === 'Đã duyệt')
})

const pendingExpenses = computed(() => {
  return expenses.value.filter(expense => !expense.status || expense.status === 'Chờ duyệt')
})

const paidExpenses = computed(() => {
  return expenses.value.filter(expense => expense.status === 'Đã thanh toán')
})

const rejectedExpenses = computed(() => {
  return expenses.value.filter(expense => expense.status === 'Từ chối')
})

const approvedExpensesTotal = computed(() => {
  return approvedExpenses.value.reduce((sum, expense) => sum + expense.amount, 0)
})

const pendingExpensesTotal = computed(() => {
  return pendingExpenses.value.reduce((sum, expense) => sum + expense.amount, 0)
})

const paidExpensesTotal = computed(() => {
  return paidExpenses.value.reduce((sum, expense) => sum + expense.amount, 0)
})

const rejectedExpensesTotal = computed(() => {
  return rejectedExpenses.value.reduce((sum, expense) => sum + expense.amount, 0)
})

const fetchCategories = async () => {
  try {
    const response = await axios.get(`${API_URLS.categories}/${API_VERSION}/categories`)
    categories.value = response.data
  } catch (error) {
    console.error('Error fetching categories:', error)
    ElMessage.error('Không thể tải danh sách danh mục')
  }
}

const editExpense = (expense: Expense) => {
  editingExpense.value = expense
  expenseForm.value = {
    payment_date: expense.payment_date,
    expense_type: expense.expense_type || '',
    category: expense.category,
    description: expense.description || '',
    amount: expense.amount,
    account_holder: expense.account_holder,
    status: expense.status || 'Chờ duyệt',
    note: expense.note || '',
    account_number: expense.account_number || '',
    bank_name: expense.bank_name || ''
  }
  showAddModal.value = true
}

const saveExpense = async () => {
  try {
    if (editingExpense.value) {
      await axios.put(`${API_URLS.expenses}/${API_VERSION}/expenses/${editingExpense.value.id}`, expenseForm.value)
      ElMessage.success('Cập nhật chi phí thành công')
    } else {
      await axios.post(`${API_URLS.expenses}/${API_VERSION}/expenses`, expenseForm.value)
      ElMessage.success('Thêm chi phí thành công')
    }
    showAddModal.value = false
    editingExpense.value = null
    expenseForm.value = {
      payment_date: '',
      expense_type: '',
      category: '',
      description: '',
      amount: 0,
      account_holder: '',
      status: 'Chờ duyệt',
      note: '',
      account_number: '',
      bank_name: ''
    }
    await fetchExpenses()
  } catch (error) {
    console.error('Error saving expense:', error)
    ElMessage.error('Có lỗi xảy ra khi lưu chi phí')
  }
}

const deleteExpense = async (expense: Expense) => {
  ElMessageBox.confirm(
    'Bạn có chắc chắn muốn xóa chi phí này?',
    'Xác nhận',
    {
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy',
      type: 'warning',
    }
  )
    .then(async () => {
      try {
        await axios.delete(`${API_URLS.expenses}/${API_VERSION}/expenses/${expense.id}`)
        ElMessage.success('Xóa chi phí thành công')
        await fetchExpenses()
      } catch (error) {
        console.error('Error deleting expense:', error)
        ElMessage.error('Có lỗi xảy ra khi xóa chi phí')
      }
    })
    .catch(() => {
      // User canceled
    })
}

const handleColumnToggle = (columnValue: string) => {
  // Check if the column is required
  const requiredColumns = ['payment_date', 'amount', 'status'];
  if (requiredColumns.includes(columnValue)) {
    ElMessage.warning('Không thể ẩn cột bắt buộc');
    return;
  }

  const index = visibleColumns.value.indexOf(columnValue);
  if (index === -1) {
    visibleColumns.value.push(columnValue);
  } else {
    visibleColumns.value.splice(index, 1);
  }
  
  // Save preferences to localStorage
  localStorage.setItem('expenseTableColumns', JSON.stringify(visibleColumns.value));
};

const handleColumnVisibilityChange = () => {
  // Ensure required columns are always visible
  const requiredColumns = ['payment_date', 'amount', 'status'];
  let changed = false;

  requiredColumns.forEach(col => {
    if (!visibleColumns.value.includes(col)) {
      visibleColumns.value.push(col);
      changed = true;
    }
  });

  if (changed) {
    ElMessage.warning('Một số cột bắt buộc không thể ẩn');
  }

  // Save preferences to localStorage
  localStorage.setItem('expenseTableColumns', JSON.stringify(visibleColumns.value));
};

const handleSortChange = (sort: SortParams) => {
  if (sort.prop) {
    currentSort.value = sort
    // Reset to first page when sorting
    currentPage.value = 1
    
    if (serverPagination.value) {
      fetchExpensesWithFilters()
    }
  }
}

const formatDate = (date: string) => {
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch {
    return date;
  }
};

const downloadDocument = (url: string) => {
  window.open(url, '_blank');
};

const handleEdit = (row: Expense) => {
  editExpense(row);
};

const handleDelete = (row: Expense) => {
  deleteExpense(row);
};

// Sorting
const currentSort = ref<SortParams>({
  prop: 'payment_date',
  order: 'descending'
})

// Update expense types helper function
const updateExpenseTypes = (data: Expense[]) => {
  const uniqueTypes = new Set<string>()
  data.forEach(expense => {
    if (expense.expense_type) {
      uniqueTypes.add(expense.expense_type)
    }
  })
  expenseTypes.value = Array.from(uniqueTypes)
}

// References for file upload
const excelUploadInput = ref<HTMLInputElement | null>(null);

// Excel import functionality
const importFromExcel = () => {
  // Trigger file input click
  if (excelUploadInput.value) {
    excelUploadInput.value.click();
  }
};

// Process Excel file upload
const handleExcelUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  
  if (!files || files.length === 0) {
    return;
  }
  
  // Store reference to loading instance
  let loadingInstance: any = null;
  
  try {
    // Show loading
    loadingInstance = ElLoading.service({
      lock: true,
      text: t('expenses.import.processing'),
      background: 'rgba(0, 0, 0, 0.7)'
    });
    
    const file = files[0];
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          ElMessage.error(t('expenses.import.errors.cannotRead'));
          loadingInstance.close();
          return;
        }
        
        // Parse Excel data
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const excelData = XLSX.utils.sheet_to_json<any>(worksheet, { header: 1 });
        
        // Validate headers
        const headers = excelData[0];
        const requiredHeaders = [
          'payment_date', 'expense_type', 'category', 'description', 
          'amount', 'account_holder', 'account_number', 'bank_name', 'status'
        ];
        
        // Check if all required headers are present
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          ElMessage.error(t('expenses.import.errors.missingHeaders', { headers: missingHeaders.join(', ') }));
          loadingInstance.close();
          return;
        }
        
        // Process data rows
        const dataRows = excelData.slice(1).filter(row => row.length > 0);
        if (dataRows.length === 0) {
          ElMessage.warning(t('expenses.import.errors.noData'));
          loadingInstance.close();
          return;
        }
        
        // Map data rows to expense objects
        const expenses = dataRows.map(row => {
          const expense: Record<string, any> = {};
          
          headers.forEach((header: string, index: number) => {
            if (header && row[index] !== undefined) {
              expense[header] = row[index];
            }
          });
          
          return expense;
        });
        
        // Validate and submit data
        await uploadExpenses(expenses);
        
        // Reset file input
        if (excelUploadInput.value) {
          excelUploadInput.value.value = '';
        }
        
      } catch (error) {
        console.error('Error processing Excel file:', error);
        ElMessage.error(t('expenses.import.errors.processingError'));
      } finally {
        loadingInstance.close();
      }
    };
    
    reader.onerror = () => {
      ElMessage.error(t('expenses.import.errors.cannotRead'));
      loadingInstance.close();
    };
    
    reader.readAsArrayBuffer(file);
    
  } catch (error) {
    console.error('Error reading Excel file:', error);
    ElMessage.error(t('expenses.import.errors.readError'));
    loadingInstance.close();
  }
};

// Upload expenses to server
const uploadExpenses = async (expenses: Record<string, any>[]) => {
  try {
    // Show confirmation dialog
    await ElMessageBox.confirm(
      t('expenses.import.confirmation', { count: expenses.length }),
      t('expenses.import.confirmTitle'),
      {
        confirmButtonText: t('expenses.import.importButton'),
        cancelButtonText: t('common.actions.cancel'),
        type: 'warning'
      }
    );
    
    // Show loading
    const loadingInstance = ElLoading.service({
      lock: true,
      text: t('expenses.import.importing'),
      background: 'rgba(0, 0, 0, 0.7)'
    });
    
    // API endpoint for batch import
    const response = await axios.post(
      `${API_URLS.expenses}/${API_VERSION}/expenses/batch`,
      { expenses }
    );
    
    loadingInstance.close();
    
    if (response.data && response.data.success) {
      ElMessage.success(t('expenses.import.success', { count: expenses.length }));
      // Refresh expenses list
      fetchExpenses();
    } else {
      ElMessage.error(t('expenses.import.errors.importError'));
    }
    
  } catch (error: any) {
    // Handle user cancel
    if (error === 'cancel') {
      return;
    }
    
    console.error('Error uploading expenses:', error);
    
    if (error.response && error.response.data && error.response.data.errors) {
      // Show detailed validation errors
      const errorMessages = error.response.data.errors.map((err: any) => 
        t('expenses.import.errors.rowError', { row: err.row, message: err.message })
      );
      
      ElMessageBox.alert(
        `<div style="max-height: 300px; overflow-y: auto;">${errorMessages.join('<br>')}</div>`,
        t('expenses.import.errors.importError'),
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: t('common.actions.close'),
          type: 'error'
        }
      );
    } else {
      ElMessage.error(t('expenses.import.errors.excelImportError'));
    }
  }
};

// Show template information and download template
const showTemplateInfo = () => {
  ElMessageBox.confirm(
    `<div>
      <p>${t('expenses.import.templateInstructions')}</p>
      <ul>
        <li><strong>payment_date</strong>: ${t('expenses.import.fields.paymentDate')}</li>
        <li><strong>expense_type</strong>: ${t('expenses.import.fields.expenseType')}</li>
        <li><strong>category</strong>: ${t('expenses.import.fields.category')}</li>
        <li><strong>description</strong>: ${t('expenses.import.fields.description')}</li>
        <li><strong>amount</strong>: ${t('expenses.import.fields.amount')}</li>
        <li><strong>account_holder</strong>: ${t('expenses.import.fields.accountHolder')}</li>
        <li><strong>account_number</strong>: ${t('expenses.import.fields.accountNumber')}</li>
        <li><strong>bank_name</strong>: ${t('expenses.import.fields.bankName')}</li>
        <li><strong>status</strong>: ${t('expenses.import.fields.status')}</li>
        <li><strong>note</strong>: ${t('expenses.import.fields.note')}</li>
      </ul>
      <p>${t('expenses.import.downloadQuestion')}</p>
    </div>`,
    t('expenses.import.templateInfoTitle'),
    {
      confirmButtonText: t('expenses.import.downloadTemplate'),
      cancelButtonText: t('common.actions.close'),
      dangerouslyUseHTMLString: true,
      type: 'info'
    }
  ).then(() => {
    downloadExcelTemplate();
  }).catch(() => {
    // User cancelled
  });
};

// Download Excel template
const downloadExcelTemplate = () => {
  // Define the template headers and sample data
  const templateData = [
    [
      'payment_date', 'expense_type', 'category', 'description', 
      'amount', 'account_holder', 'account_number', 'bank_name', 
      'status', 'note'
    ],
    // Example 1: Business meal expense
    [
      '2023-06-01', t('expenses.import.examples.mealType'), t('expenses.import.examples.mealCategory'), t('expenses.import.examples.mealDesc'), 
      '500000', t('expenses.import.examples.person1'), '1234567890', 'Vietcombank', 
      t('expenses.status.pending'), t('expenses.import.examples.mealNote')
    ],
    // Example 2: Office supplies
    [
      '2023-06-05', t('expenses.import.examples.suppliesType'), t('expenses.import.examples.suppliesCategory'), t('expenses.import.examples.suppliesDesc'), 
      '350000', t('expenses.import.examples.person2'), '9876543210', 'Agribank', 
      t('expenses.status.approved'), t('expenses.import.examples.suppliesNote')
    ],
    // Example 3: Transportation
    [
      '2023-06-10', t('expenses.import.examples.transportType'), t('expenses.import.examples.transportCategory'), t('expenses.import.examples.transportDesc'), 
      '180000', t('expenses.import.examples.person3'), '5678901234', 'BIDV', 
      t('expenses.status.paid'), t('expenses.import.examples.transportNote')
    ],
    // Example 4: Utility bill
    [
      '2023-06-15', t('expenses.import.examples.utilityType'), t('expenses.import.examples.utilityCategory'), t('expenses.import.examples.utilityDesc'), 
      '1200000', t('expenses.import.examples.person4'), '1357924680', 'Techcombank', 
      t('expenses.status.pending'), t('expenses.import.examples.utilityNote')
    ]
  ];
  
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(templateData);
  
  // Set column widths for better readability
  const columnWidths = [
    { wch: 12 },  // payment_date
    { wch: 15 },  // expense_type
    { wch: 15 },  // category
    { wch: 30 },  // description
    { wch: 10 },  // amount
    { wch: 15 },  // account_holder
    { wch: 15 },  // account_number
    { wch: 15 },  // bank_name
    { wch: 12 },  // status
    { wch: 20 },  // note
  ];
  
  worksheet['!cols'] = columnWidths;
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, t('expenses.import.templateSheetName'));
  
  // Generate Excel file and trigger download
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, t('expenses.import.templateFileName'));
};

onMounted(() => {
  // Initialize default visible columns
  const defaultVisibleColumns = availableColumns
    .filter(col => col.defaultVisible)
    .map(col => col.value);
  
  // Load user preferences if available
  const savedColumns = localStorage.getItem('expenseVisibleColumns');
  if (savedColumns) {
    try {
      visibleColumns.value = JSON.parse(savedColumns);
    } catch (e) {
      visibleColumns.value = defaultVisibleColumns;
    }
  } else {
    visibleColumns.value = defaultVisibleColumns;
  }
  
  // Load pagination preferences
  const savedPageSize = localStorage.getItem('expensePageSize');
  if (savedPageSize) {
    const parsedSize = parseInt(savedPageSize, 10);
    if (!isNaN(parsedSize) && [10, 20, 50, 100].includes(parsedSize)) {
      pageSize.value = parsedSize;
    }
  }
  
  // Fetch expense data
  fetchExpenses();
  
  // Fetch categories
  fetchCategories();
});
</script>

<style scoped>
.expenses-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Filter styles */
.filter-wrapper {
  margin-bottom: 12px;
}

.filter-container {
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.filter-primary-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background-color: #f9fbfd;
  border-bottom: 1px solid #edf1f7;
}

.filter-section {
  display: flex;
  align-items: center;
}

.left-section {
  flex: 0 0 auto;
  margin-right: auto;
}

.search-section {
  flex: 1;
  max-width: 850px;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.filter-item {
  flex: 1;
  min-width: 120px;
}

.date-filter {
  flex: 1.8;
  min-width: 250px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  margin-left: 8px;
}

.action-button {
  padding: 8px;
  min-width: 36px;
  min-height: 36px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e6ed;
}

.action-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}

.add-expense-btn {
  padding: 12px 16px;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(var(--el-color-primary-rgb), 0.3);
  transition: all 0.2s ease;
}

.add-expense-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--el-color-primary-rgb), 0.4);
}

.import-btn {
  margin-left: 10px;
  padding: 12px 16px;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(var(--el-color-success-rgb), 0.3);
  transition: all 0.2s ease;
}

.import-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--el-color-success-rgb), 0.4);
}

.date-range-picker {
  width: 100%;
}

.compact-select {
  width: 100%;
}

:deep(.el-input__wrapper) {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid #e0e6ed !important;
  border-radius: 4px !important;
  padding: 0 12px;
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 1px 8px rgba(18, 67, 89, 0.15) !important;
  border-color: var(--el-color-primary) !important;
}

:deep(.el-range-editor.el-input__wrapper) {
  padding: 0 8px;
}

.filter-advanced {
  border-top: 1px solid var(--neutral-200);
  padding: 16px;
  background-color: #fbfcfe;
}

.filter-header {
  margin-bottom: 16px;
}

.filter-header h4 {
  font-size: 14px;
  font-weight: 600;
  color: var(--neutral-700);
  margin: 0;
}

.advanced-form {
  width: 100%;
}

.advanced-form .el-form-item {
  margin-bottom: 8px;
}

.full-width {
  width: 100%;
}

.active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 12px 16px 16px;
  background-color: #fff;
}

.filter-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--neutral-600);
  margin-right: 8px;
  display: flex;
  align-items: center;
}

.filter-label:before {
  content: "";
  display: inline-block;
  width: 4px;
  height: 4px;
  background-color: var(--neutral-400);
  border-radius: 50%;
  margin-right: 6px;
}

.filter-tag {
  font-size: 12px;
  height: 24px;
  line-height: 22px;
  padding: 0 8px;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.filter-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Table styles */
.table-container {
  border-radius: 6px;
  box-shadow: var(--shadow-sm);
}

:deep(.el-card__body) {
  padding: 12px !important;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--neutral-200);
}

.table-left-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.batch-actions {
  margin-left: 10px;
}

.total-section {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  background-color: var(--primary-ultra-light);
  padding: 5px 10px;
  border-radius: 4px;
}

.total-section strong {
  font-weight: 600;
  color: var(--primary-dark);
}

.filter-tools {
  display: flex;
  gap: 6px;
  align-items: center;
}

/* Table styling */
:deep(.el-table) {
  --el-table-header-bg-color: var(--primary-ultra-light);
  --el-table-border-color: var(--neutral-200);
  border-radius: 8px;
  overflow: hidden;
  font-size: 13px;
}

:deep(.el-table__header-wrapper) {
  position: sticky;
  top: 0;
  z-index: 1;
}

:deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
  background-color: rgba(var(--el-color-primary-rgb), 0.02);
}

:deep(.el-table__row:hover td) {
  background-color: rgba(var(--el-color-primary-rgb), 0.05) !important;
}

:deep(.el-table__row-selection) {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.el-table .cell) {
  padding: 3px 8px;
  line-height: 1.3;
}

:deep(.el-dialog) {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: var(--neutral-700);
}

.load-more {
  margin-top: 10px;
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Pagination */
.pagination-container {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 0;
}

.expense-pagination {
  margin-right: 20px;
}

.expense-table {
  margin-bottom: 10px;
  /* Add responsive table styles */
  width: 100%;
  overflow-x: auto;
}

/* Make table responsive on small screens */
@media (max-width: 768px) {
  .expense-table {
    max-width: 100%;
    overflow-x: auto;
  }
  
  .pagination-container {
    flex-direction: column;
    align-items: center;
  }
  
  .expense-pagination {
    margin-right: 0;
    margin-top: 10px;
  }
}

/* Dialog and forms */
.column-selector-item {
  margin: 6px 0;
  padding-left: 2px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

:deep(.el-dialog__body) {
  padding: 12px;
}

:deep(.el-dialog__header) {
  padding: 12px;
  margin-right: 0;
}

:deep(.el-dialog__footer) {
  padding: 8px 12px 12px;
}

/* Responsive design */
@media (max-width: 1400px) {
  .date-filter {
    flex: 1.5;
    min-width: 220px;
  }
}

@media (max-width: 1200px) {
  .filter-primary-row {
    flex-wrap: wrap;
    padding: 12px 12px 6px;
  }
  
  .search-section {
    width: 100%;
    margin-top: 8px;
  }
  
  .search-container {
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-start;
  }
  
  .action-buttons {
    margin-left: 0;
    margin-top: 6px;
  }
  
  .filter-item, .date-filter {
    max-width: none;
    width: calc(33.33% - 6px);
  }
  
  .date-filter {
    width: calc(33.33% - 6px);
  }
}

@media (max-width: 900px) {
  .filter-item, .date-filter {
    width: calc(50% - 4px);
  }
}

@media (max-width: 768px) {
  .expenses-container {
    gap: 8px;
  }
  
  .filter-primary-row {
    padding: 10px 10px 5px;
  }
  
  .filter-advanced {
    padding: 10px 10px 5px;
  }
  
  .active-filters {
    padding: 6px 10px 10px;
  }
  
  .filter-primary-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .left-section {
    width: 100%;
    justify-content: center;
    margin-bottom: 8px;
  }
  
  .search-container {
    flex-direction: column;
    width: 100%;
    gap: 6px;
  }
  
  .filter-item, .date-filter {
    width: 100%;
  }
  
  .action-buttons {
    margin-top: 6px;
    justify-content: flex-end;
    width: 100%;
  }
  
  :deep(.el-card__body) {
    padding: 10px !important;
  }
}

/* Thêm style cho button dạng icon */
.icon-button {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 4px;
  position: relative;
}

.button-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: white;
  color: var(--danger);
  font-size: 12px;
  font-weight: bold;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 0 0 1px var(--danger);
}

/* Thêm style cho column selector popover */
.column-selector-content {
  padding: 5px 0;
}

.column-selector-header {
  padding: 0 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.column-selector-title {
  font-weight: 600;
  font-size: 14px;
  color: var(--neutral-900);
}

.column-selector-actions {
  display: flex;
  gap: 8px;
}

.column-selector-list {
  max-height: 240px;
  overflow-y: auto;
  padding: 5px 12px;
}

.column-selector-item {
  margin: 8px 0;
}

.column-selector-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 12px;
}

/* Add styles for the statistics section */
.stats-container {
  margin-bottom: 12px;
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.stats-wrapper {
  display: flex;
  justify-content: space-between;
  padding: 0;
}

.stat-item {
  flex: 1;
  position: relative;
  padding: 16px;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.stat-item:active {
  transform: translateY(1px);
}

.stat-count {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
}

.stat-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

/* Status-specific colors */
.approved-stat {
  border-top: 3px solid var(--el-color-success);
}

.approved-stat .stat-count {
  color: var(--el-color-success);
}

.pending-stat {
  border-top: 3px solid var(--el-color-warning);
}

.pending-stat .stat-count {
  color: var(--el-color-warning);
}

.paid-stat {
  border-top: 3px solid var(--el-color-primary);
}

.paid-stat .stat-count {
  color: var(--el-color-primary);
}

.rejected-stat {
  border-top: 3px solid var(--el-color-danger);
}

.rejected-stat .stat-count {
  color: var(--el-color-danger);
}

.stat-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--neutral-700);
  margin-bottom: 4px;
}

.stat-amount {
  font-size: 14px;
  font-weight: 500;
  color: var(--neutral-600);
  white-space: nowrap;
}

.stat-divider {
  width: 1px;
  background-color: var(--neutral-200);
}

/* Responsive design for stats */
@media (max-width: 768px) {
  .stats-wrapper {
    flex-wrap: wrap;
  }
  
  .stat-item {
    min-width: 50%;
    border-bottom: 1px solid var(--neutral-200);
  }
  
  .stat-divider {
    display: none;
  }
}

@media (max-width: 480px) {
  .stat-item {
    min-width: 100%;
  }
}

.column-selector {
  margin-left: 8px;
}

.el-dropdown-menu {
  max-height: 400px;
  overflow-y: auto;
}

.el-dropdown-item.is-checked {
  color: var(--el-color-primary);
}

.el-checkbox {
  width: 100%;
  margin-right: 0;
  display: flex;
  align-items: center;
}
</style> 